import {
  Component,
  computed,
  effect,
  inject,
  Input,
  Signal,
  signal,
} from "@angular/core";
import {CommonModule} from "@angular/common";
import {ProductionLineService} from "src/app/features/admin/data-access/production-line.service";
import {NavigationService} from "src/app/shared/data-access/navigation.service";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {ActivatedRoute, Router, RouterModule} from "@angular/router";
import {MatInputModule} from "@angular/material/input";
import {MatSelectModule} from "@angular/material/select";
import {SaveCancelButtonsComponent} from "../../../components/save-cancel-buttons/save-cancel-buttons.component";
import {WorkstationService} from "src/app/features/admin/data-access/workstation.service";
import {Workstation} from "src/app/shared/interfaces/workstation";
import {SelectionService} from "src/app/services/selection.service";

type FormAction = "create" | "edit" | "";

@Component({
  selector: "app-workstation-form",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    RouterModule,
    MatSelectModule,
    SaveCancelButtonsComponent,
  ],
  templateUrl: "./workstation-form.component.html",
  styleUrls: ["./workstation-form.component.scss"],
})
export class WorkstationFormComponent {
  @Input() title: string;
  productionLineService = inject(ProductionLineService);
  workstationService = inject(WorkstationService);
  navigationService = inject(NavigationService);
  selectionService = inject(SelectionService);
  formBuilder = inject(FormBuilder);
  router = inject(Router);
  action = signal<FormAction>("edit");
  nextPosition = signal<number>(99);
  route = inject(ActivatedRoute);

  workstationForm: FormGroup = this.formBuilder.group({
    name: ["", Validators.required],
    workstation_id: [{value: "", disabled: true}],
    position: [99, Validators.required],
    productionLine: ["", Validators.required],
    status: [true, Validators.required],
    productionLineIndex: [0, Validators.required],
  });

  protected workstationBeingEdited = computed(() => {
    const url = this.navigationService.currentUrl();
    const workstationUuid = url.split("workstation/")[1]?.split("?")[0];
    if (workstationUuid) {
      return (
        this.workstationService
          .workstations()
          .find((workstation) => workstation.id === workstationUuid) || null
      );
    } else {
      return null;
    }
  });

  constructor() {
    this.action.set(
      this.route.snapshot.queryParams["formAction"] as FormAction
    );
    // get position of last workstation
    this.nextPosition.set(
      this.workstationService.filteredWorkstations().length > 0
        ? Math.max(
        ...this.workstationService
          .filteredWorkstations()
          .map((ws) => ws.position)
      ) + 1
        : 0
    );
    effect(() => {
      this.workstationForm.patchValue({position: this.nextPosition()});

      const workstation = this.workstationBeingEdited();
      if (workstation) {
        this.workstationForm.patchValue({
          name: workstation.name,
          workstation_id: (workstation.position + 1)
            .toString()
            .padStart(3, "0"),
          description: workstation.description,
          position: workstation.position,
          productionLine: workstation.production_line.id,
          productionLineIndex: workstation.production_line_index,
          status: workstation.is_active,
        });
      }
    });
  }

  ngOnInit() {
    this.getWorkstationFromUrl();
    this.selectionService.currentProductionLine.subscribe((value) => {
      this.workstationForm.patchValue({productionLine: value});
    });
  }

  getWorkstationFromUrl(): Signal<Workstation | null> {
    return this.workstationBeingEdited;
  }

  onSave() {
    if (this.action() === "create" && this.workstationForm.valid) {
      this.workstationService.add$.next({
        name: this.workstationForm.get("name")!.value,
        position: this.workstationForm.get("position")!.value,
        production_line: {
          id: this.workstationForm.get("productionLine")!.value,
          name: "",
          shortcode: "",
        },
        production_line_index: this.workstationForm.get("productionLineIndex")!
          .value,
        is_active: this.workstationForm.get("status")!.value,
      });
      alert("Neuer Arbeitsplatz hinzugefügt");
    } else if (this.action() === "edit" && this.workstationForm.valid) {
      this.workstationService.update$.next({
        id: this.workstationBeingEdited()?.id,
        name: this.workstationForm.get("name")!.value,
        position: this.workstationForm.get("position")!.value,
        production_line: {
          id: this.workstationForm.get("productionLine")!.value,
          name: "",
          shortcode: "",
        },
        production_line_index: 0,
        is_active: this.workstationForm.get("status")!.value,
      });
      alert("Änderungen gespeichert");
    }
    this.router.navigate(["/admin/workstations"]);
  }
}
